import { useState } from "react";
import { httpGetWithAuth } from "../../helpers/axiosUtils";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import config from "../../config";
import { getApi } from "../../pages/AppApis";
import { showErrorToast } from "../../pages/utils";

const useGetProperties = () => {
    const [loadingProperties, setLoadingProperties] = useState(false);
    const [propertiesData, setPropertiesData] = useState([]);
    const authUserToken = localStorage.getItem("authUser");
    const authUser = authUserToken ? authUserToken.replace(/^"|"$/g, "") : "";
    const googleMapsApiKey = config.GOOGLE_API_KEY;
    const navigate = useNavigate();

    const fetchProfileData = async () => {
        try {
            const profileUrl = `/user/profile`;
            const response = await getApi(profileUrl);
            if (response.response) {
                return response.response;
            } else if (response?.error === 401) {
                navigate("/login");
            } else {
                showErrorToast("Unspected error occure", {
                    autoClose: 3000,
                    pauseOnHover: false,
                });
                return null;
            }
        } catch (error) {
            // console.error("Error fetch  ing profile data:", error);
            return null;
        }
    };

    const fetchPropertiesApi = async (profileData) => {
        setLoadingProperties(true);
        try {
            const res = await httpGetWithAuth("/user/properties", {}, authUser);
            if (res) {
                let filterProperties = res.filter((property) => property.owner_info?.user_id !== profileData.uuid);

                // Populate missing coordinates
                filterProperties = await Promise.all(
                    filterProperties.map(async (property) => {
                        if (property.latitude === 0 || property.longitude === 0) {
                            const geoResponse = await fetch(
                                `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(property.address)}&key=${googleMapsApiKey}`
                            );
                            const geoData = await geoResponse.json();
                            if (geoData.status === "OK" && geoData.results.length > 0) {
                                property.latitude = geoData.results[0].geometry.location.lat;
                                property.longitude = geoData.results[0].geometry.location.lng;
                            }
                        }
                        return property;
                    })
                );

                setPropertiesData(filterProperties);
                setLoadingProperties(false);
                return { filterProperties };
            }
            setLoadingProperties(false);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                showErrorToast("Session expired", {
                    autoClose: 3000,
                    pauseOnHover: false,
                });
                localStorage.removeItem("authUser");
                navigate("/login");
            }
            setLoadingProperties(false);
        }
    };

    const searchProperties = async (locationArrayFinal, filters) => {
        try {
            const profileData = await fetchProfileData();
            if (!profileData) {
                throw new Error("Failed to fetch profile data");
            }

            const params = new URLSearchParams();

            // Add filter params
            if (filters.forSale) params.append("status", filters.forSale);
            if (filters.price) {
                const [minPrice, maxPrice] = filters.price.split(" - ").map((p) => p.replace("$", "").replace("K", ""));
                if (minPrice) params.append("min_price", minPrice);
                if (maxPrice) params.append("max_price", maxPrice);
            }
            if (filters.beds) params.append("bedrooms", filters.beds.replace(" Beds", ""));
            if (filters.baths) params.append("bathrooms", filters.baths.replace(" Baths", ""));
            if (filters.homeType) params.append("home_type", filters.homeType);

            const queryString = params.toString();
            const url = `/user/properties/search?${queryString}`;
            const response = await httpGetWithAuth(url, {}, authUser);

            let filterProperties = response;

            if (!queryString) {
                // Only filter out properties with the same user_id if no search filters are applied
                filterProperties = response.filter((property) => property.owner_info?.user_id !== profileData.uuid);
            }

            // Apply additional filters on the client side if needed
            if (filters.beds) {
                filterProperties = filterProperties.filter((property) => property.beds == filters.beds.replace(" Beds", ""));
            }
            if (filters.baths) {
                filterProperties = filterProperties.filter((property) => property.baths == filters.baths.replace(" Baths", ""));
            }

            setPropertiesData(filterProperties);
            return filterProperties;
        } catch (error) {
            console.error("Error fetching properties:", error);
            if (error.response) {
                console.error("Response data:", error.response.data);
                console.error("Response status:", error.response.status);
                console.error("Response headers:", error.response.headers);
            }
        }
    };

    return {
        fetchPropertiesApi,
        searchProperties,
        loadingProperties,
        propertiesData,
    };
};

export default useGetProperties;
